import React, { createContext, useEffect, useRef, useState } from 'react';

// 커스텀 훅
import useCallbackRef from 'hooks/useCallbackRef';
import useCarousel from 'hooks/useCarousel';

// 스타일
import FilterButton from './FilterButton';
import { FilterDefault, FilterGray } from './index';
import {
	ChipsCarouselGroup,
	ChipsCarouselGroupInner,
	ChipsGroup,
	ChipsLargeGroup,
} from './style';
// 타입
import { IChipsRadioGroup } from './type';

// chips의 목록을 관리하는 state
const CoreRadioChips = ({
	group_type,
	style_type,
	chips_list,
	_width = '0',
	chips_width,
	_disabled,
	dispatch,
	selected_chips,
	setSelectedChips,
	_onClick,
}: IChipsRadioGroup) => {
	const [radio_chips, setRadioChips] = useState(chips_list ?? []);

	useEffect(() => {
		if (chips_list) setRadioChips(chips_list);
	}, [chips_list]);

	// Carousel Dom과 Carousel-inner Dom
	const [group_dom, group_ref] = useCallbackRef();
	const [group_inner_dom, group_inner_ref] = useCallbackRef();

	// Carousel에 필요한 값들을 반환하는 custom hook
	// (왼쪽에서부터) 애니메이션 컨트롤러 / 왼쪽으로 드래그 할 수 있는 수치 / 드래그 할 수 있는 상태인지 여부
	const [chipsGroupControl, drag_constraints, drag_condition] = useCarousel(
		group_dom,
		group_inner_dom,
		[radio_chips, selected_chips],
	);
	// 값이 변경될 때마다 그 값을 dispatch.(단 초기값일 때는 제외)
	useEffect(() => {
		if (selected_chips === '' || !selected_chips || dispatch === undefined) return;
		dispatch(selected_chips);
	}, [selected_chips]);

	if (group_type === 'large')
		return (
			<ChipsLargeGroup _width={_width} onClick={_onClick}>
				{radio_chips.map((chips, idx) => (
					<CoreRadioChips.FilterButton
						key={idx}
						selected={selected_chips ?? ''}
						setSelected={setSelectedChips}
						value={chips}
						_width={chips_width}
					/>
				))}
			</ChipsLargeGroup>
		);

	if (group_type === 'carousel')
		return (
			<ChipsCarouselGroup
				ref={group_ref}
				_width={_width}
				draggable={drag_condition === 'x' ? true : false}
				onClick={_onClick}
			>
				<ChipsCarouselGroupInner
					drag={drag_condition}
					dragConstraints={drag_constraints}
					animate={chipsGroupControl}
					ref={group_inner_ref}
				>
					{radio_chips.map((chips, idx) => {
						if (_disabled?.includes(idx))
							return (
								<CoreRadioChips.FilterDefault
									key={idx}
									selected={selected_chips ?? ''}
									// setSelected={setSelectedChips}
									_disabled={true}
									value={chips}
								/>
							);

						return (
							<React.Fragment key={idx}>
								{style_type === 'default' ? (
									<CoreRadioChips.FilterDefault
										key={idx}
										selected={selected_chips ?? ''}
										setSelected={setSelectedChips}
										// is_dragging={is_dragging}
										value={chips}
									/>
								) : (
									<CoreRadioChips.FilterGray
										key={idx}
										selected={selected_chips ?? ''}
										setSelected={setSelectedChips}
										value={chips}
									/>
								)}
							</React.Fragment>
						);
					})}
				</ChipsCarouselGroupInner>
			</ChipsCarouselGroup>
		);

	if (group_type === 'multi_line')
		return (
			<ChipsGroup _width={_width} group_type="multi_line" onClick={_onClick}>
				{radio_chips.map((chips, idx) =>
					style_type === 'default' ? (
						<CoreRadioChips.FilterDefault
							key={idx}
							selected={selected_chips ?? ''}
							setSelected={setSelectedChips}
							value={chips}
						/>
					) : (
						<CoreRadioChips.FilterGray
							key={idx}
							selected={selected_chips ?? ''}
							setSelected={setSelectedChips}
							value={chips}
						/>
					),
				)}
			</ChipsGroup>
		);

	return null;
};

CoreRadioChips.FilterDefault = FilterDefault;
CoreRadioChips.FilterGray = FilterGray;
CoreRadioChips.FilterButton = FilterButton;

export default CoreRadioChips;

// 사용방법
//
// 1. 이 컴포넌트를 import하는 부모 컴포넌트에서 전역저장소의 데이터를 구독할 함수와, 디스패치함수를 준비한다.
//
// 2. props dispatch를 통해 이 컴포넌트에 dispatch함수를 전달한다.
//
// 3. 구독함수를 통해 선택된 radio의 값을 얻을 수 있다.
//
// 그 외의 props
// - group_type : 캐로셀인지 멀티라인인지
// - style_type : default인지 gray인지
// - chips_list : chips그룹의 리스트
// - width : chips그룹의 넓이
